/**
 * Ensure tool calls have responses (Two-Role Model)
 *
 * In the two-role model:
 * - tool_use blocks are content within AssistantMessage (ai type)
 * - tool_result blocks are content within UserMessage (human type)
 *
 * This function checks if an AI message with tool_use blocks is followed
 * by a human message containing tool_result content blocks.
 *
 * @module features/chat/lib/ensure-tool-responses
 */

import { v4 as uuidv4 } from "uuid";
import { Message } from "../types/sdk";
import { DO_NOT_RENDER_ID_PREFIX } from "@/constants";

/**
 * Tool result content block structure (matches Anthropic API)
 */
interface ToolResultContentBlock {
  type: "tool_result";
  tool_use_id: string;
  content: string;
  is_error?: boolean;
}

/**
 * UserMessage with tool_result content blocks
 */
interface UserMessageWithToolResults {
  id: string;
  type: "human";
  content: ToolResultContentBlock[];
}

/**
 * Ensures that all tool_use blocks in AI messages have corresponding tool_result responses.
 *
 * In the two-role model, if an AI message has tool_use blocks in its content,
 * the following message should be a human message containing tool_result content blocks.
 *
 * @param messages - Array of messages to check
 * @returns Array of UserMessages with tool_result content blocks that need to be added
 */
export function ensureToolCallsHaveResponses(
  messages: Message[],
): UserMessageWithToolResults[] {
  const newMessages: UserMessageWithToolResults[] = [];

  messages.forEach((message, index) => {
    if (message.type !== "ai") {
      // If it's not an AI message, we can ignore.
      return;
    }

    // Check if this AI message has tool_use blocks in its content
    const content = message.content;
    if (!Array.isArray(content)) {
      return;
    }

    const toolUseBlocks = content.filter(
      (block: any) => block.type === "tool_use"
    );

    if (toolUseBlocks.length === 0) {
      // No tool_use blocks, nothing to check
      return;
    }

    // Check if the following message has tool_result content blocks
    const followingMessage = messages[index + 1];

    if (followingMessage && followingMessage.type === "human") {
      // Check if the human message contains tool_result content blocks
      const followingContent = followingMessage.content;
      if (Array.isArray(followingContent)) {
        const hasToolResults = followingContent.some(
          (block: any) => block.type === "tool_result"
        );
        if (hasToolResults) {
          // Following message already has tool results, we're good
          return;
        }
      }
    }

    // Create a new human message with tool_result content blocks
    const toolResultBlocks: ToolResultContentBlock[] = toolUseBlocks.map(
      (block: any) => ({
        type: "tool_result" as const,
        tool_use_id: block.id ?? "",
        content: "Successfully handled tool call.",
        is_error: false,
      })
    );

    newMessages.push({
      id: `${DO_NOT_RENDER_ID_PREFIX}${uuidv4()}`,
      type: "human",
      content: toolResultBlocks,
    });
  });

  return newMessages;
}
